import * as Sentry from "@sentry/react";

/**
 * Centralized Logging Utility
 *
 * Provides structured logging with Sentry integration for error tracking.
 * - Console logs are disabled in production builds
 * - All errors are automatically sent to Sentry for monitoring
 * - Warnings are also sent to Sentry for production monitoring
 */

// Determine environment
const isDevelopment = process.env.NODE_ENV === "development";

// Track if Sentry has been initialized
let sentryInitialized = false;

// Set Sentry initialized flag (called from sentry.ts after initialization)
export const setSentryInitialized = (initialized: boolean) => {
  sentryInitialized = initialized;
};

// Log levels enum
enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

// Context type for additional log data
type LogContext = Record<string, any>;

/**
 * Checks if an error is from a browser extension (MetaMask, etc.)
 * These errors are not relevant to the application and should be filtered out
 */
const isExtensionError = (error: any, message?: string): boolean => {
  if (!error && !message) return false;

  const errorString = error?.toString()?.toLowerCase() || "";
  const errorMessage = error?.message?.toLowerCase() || "";
  const messageString = message?.toLowerCase() || "";

  // Common browser extension error patterns
  const extensionPatterns = [
    "metamask",
    "wallet",
    "ethereum",
    "web3",
    "chrome-extension://",
    "moz-extension://",
    "safari-extension://",
    "extension://",
    "failed to connect to metamask",
  ];

  const combinedString = `${errorString} ${errorMessage} ${messageString}`;
  return extensionPatterns.some((pattern) => combinedString.includes(pattern));
};

/**
 * Logger class providing structured logging with Sentry integration
 */
class Logger {
  /**
   * Log an error - Always sent to Sentry for production monitoring
   * @param message Error message
   * @param context Additional context data
   */
  error(message: string, context?: LogContext): void {
    // Filter out browser extension errors (MetaMask, etc.)
    if (isExtensionError(context?.error, message)) {
      // Silently ignore extension errors - they're not relevant to the app
      return;
    }

     
    if (isDevelopment) {
      console.error(`[ERROR] ${message}`, context);
    }

    // Always send errors to Sentry for production monitoring (if initialized)
    try {
      if (sentryInitialized) {
        const errorToSend = context?.error instanceof Error ? context.error : new Error(message);

        Sentry.captureException(errorToSend, {
          level: "error",
          tags: {
            logger: "true",
          },
          extra: {
            message,
            ...(context &&
              Object.keys(context).reduce((acc, key) => {
                if (key !== "error") {
                  acc[key] = context[key];
                }
                return acc;
              }, {} as LogContext)),
          },
        });
      }
    } catch (sentryError) {
      // Silently fail if Sentry is not available - don't break the app
       
      if (isDevelopment) {
        console.warn("[Logger] Failed to send error to Sentry:", sentryError);
      }
    }
  }

  /**
   * Log a warning - Sent to Sentry for production monitoring
   * @param message Warning message
   * @param context Additional context data
   */
  warn(message: string, context?: LogContext): void {
     
    if (isDevelopment) {
      console.warn(`[WARN] ${message}`, context);
    }

    // Send warnings to Sentry for production monitoring (if initialized)
    try {
      if (sentryInitialized) {
        Sentry.captureMessage(message, {
          level: "warning",
          tags: {
            logger: "true",
          },
          extra: context || {},
        });
      }
    } catch (sentryError) {
      // Silently fail if Sentry is not available - don't break the app
       
      if (isDevelopment) {
        console.warn("[Logger] Failed to send warning to Sentry:", sentryError);
      }
    }
  }

  /**
   * Log an info message - Added as breadcrumb for context (helps with error debugging)
   * @param message Info message
   * @param context Additional context data
   */
  info(message: string, context?: LogContext): void {
     
    if (isDevelopment) {
      console.info(`[INFO] ${message}`, context);
    }

    // Add as breadcrumb for context (helps with error debugging)
    try {
      if (sentryInitialized) {
        Sentry.addBreadcrumb({
          message,
          level: "info",
          data: context,
        });
      }
    } catch (sentryError) {
      // Silently fail if Sentry is not available - don't break the app
       
      if (isDevelopment) {
        console.warn("[Logger] Failed to add breadcrumb to Sentry:", sentryError);
      }
    }
  }

  /**
   * Log a debug message - Development only (NOT sent to Sentry to reduce noise)
   * @param message Debug message
   * @param context Additional context data
   */
  debug(message: string, context?: LogContext): void {
     
    if (isDevelopment) {
      console.debug(`[DEBUG] ${message}`, context);
    }
    // Debug logs are NOT sent to Sentry to reduce noise
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing purposes
export { LogLevel };
